import { createContext, useState, useEffect, useRef } from "react";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const storedUser = localStorage.getItem("auth");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [showWarning, setShowWarning] = useState(false);

  const logoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  const LOGOUT_TIME = 180_000;  // Total inactivity limit (3 minutes)
  const WARNING_TIME = 165_000;

  // 🔐 Logout function
  const logout = () => {
    setAuth(null);
    localStorage.removeItem("auth");
    setShowWarning(false); // hide warning immediately
    clearTimeout(logoutTimerRef.current);
    clearTimeout(warningTimerRef.current);
    window.location.href = "/";
  };

  // 🔄 Reset timers only if logged in
  const resetTimers = () => {
    if (!auth) return;

    clearTimeout(logoutTimerRef.current);
    clearTimeout(warningTimerRef.current);
    setShowWarning(false);

    warningTimerRef.current = setTimeout(() => setShowWarning(true), WARNING_TIME);
    logoutTimerRef.current = setTimeout(() => logout(), LOGOUT_TIME);
  };

  // Only start timers and attach events if logged in
  useEffect(() => {
    if (!auth) {
      // clear any existing timers if logged out
      clearTimeout(logoutTimerRef.current);
      clearTimeout(warningTimerRef.current);
      setShowWarning(false);
      return;
    }

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    const handleActivity = () => resetTimers();

    events.forEach((event) => window.addEventListener(event, handleActivity));

    // Start timers after login
    resetTimers();

    return () => {
      events.forEach((event) => window.removeEventListener(event, handleActivity));
      clearTimeout(logoutTimerRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [auth]); // only run this effect when `auth` changes

  // Persist auth to localStorage
  useEffect(() => {
    if (auth) {
      localStorage.setItem("auth", JSON.stringify(auth));
    } else {
      localStorage.removeItem("auth");
    }
  }, [auth]);

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth,
        logout,
        showWarning,
        resetTimers,
        setShowWarning,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
